import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { Reveal } from "../Reveal";
import { apiClient, formatApiErrorDetail } from "../../lib/api";

const QUESTIONS = [
  { q: "If ransomware hit tonight, could you restore every file by tomorrow morning?", gap: "No tested, immutable backup" },
  { q: "Does every email and Microsoft 365 sign-in require a second verification step (MFA)?", gap: "Accounts without MFA protection" },
  { q: "Are security patches installed automatically on every device — with nobody having to remember?", gap: "Devices patching on hope" },
  { q: "When IT breaks at 7am before a client meeting, does a real person answer within 15 minutes?", gap: "No guaranteed response time" },
  { q: "Do you have a written IT budget and roadmap for the next 12 months?", gap: "No IT budget or roadmap" },
];

const tierFor = (score) => {
  if (score <= 1)
    return {
      title: "Your IT is running on hope.",
      body: "Most of the protection insurers and clients now expect simply isn't in place. Every gap listed above is fixable — closing them is exactly what the Sempresera Standard does, for one flat per-user rate.",
    };
  if (score <= 3)
    return {
      title: "Covered in places. Exposed in others.",
      body: "Partial protection is where most small businesses live — and it is exactly what attackers count on. The gaps you just identified are the ones that turn a bad day into a bad year.",
    };
  if (score === 4)
    return {
      title: "Strong foundation — one gap left.",
      body: "You're ahead of most businesses your size. One open gap is still one open door — a short consultation can close it for good.",
    };
  return {
    title: "Excellent — you're in rare company.",
    body: "Your bases are covered. A free consultation can confirm it with evidence, and keep it that way as threats evolve.",
  };
};

export const HealthCheck = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [phase, setPhase] = useState("quiz");
  const [form, setForm] = useState({ name: "", company: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const answer = (yes) => {
    const next = [...answers, yes];
    setAnswers(next);
    if (next.length === QUESTIONS.length) setPhase("result");
    else setStep(step + 1);
  };

  const reset = () => {
    setStep(0);
    setAnswers([]);
    setPhase("quiz");
    setSent(false);
  };

  const score = answers.filter(Boolean).length;
  const tier = tierFor(score);
  const gaps = QUESTIONS.filter((_, i) => answers[i] === false).map((q) => q.gap);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const summary = QUESTIONS.map((q, i) => `${answers[i] ? "YES" : "NO"} — ${q.q}`).join("\n");
      await apiClient.post("/health-check", { ...form, score, answers: summary, verdict_title: tier.title, verdict_body: tier.body, gaps });
      setSent(true);
      toast.success("Your results are on their way to our team.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section className="border-t border-white/5" data-testid="health-check-section">
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
        <Reveal>
          <p className="eyebrow">IT health check</p>
          <h2 className="mt-6 max-w-4xl font-display text-4xl font-black leading-[1.02] tracking-tighter md:text-6xl lg:text-7xl">
            How healthy is <span className="text-brand">your IT?</span>
          </h2>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#C9D2DE] md:text-lg">
            Five yes-or-no questions, sixty seconds, zero jargon. See exactly where your business stands — and what
            it would take to close the gaps.
          </p>
        </Reveal>

        <div className="glass mx-auto mt-14 max-w-3xl p-8 md:p-10" data-testid="health-check-card">
          {phase === "quiz" ? (
            <div>
              <div className="flex items-center justify-between">
                <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#A8B4C2]" data-testid="health-check-progress">
                  Question {step + 1} of {QUESTIONS.length}
                </p>
                <div className="h-1 w-32 bg-white/10">
                  <div className="h-1 bg-brand transition-[width] duration-300" style={{ width: `${(step / QUESTIONS.length) * 100}%` }} />
                </div>
              </div>
              <p className="mt-8 font-display text-xl font-bold leading-snug text-white md:text-2xl" data-testid="health-check-question">
                {QUESTIONS[step].q}
              </p>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <button onClick={() => answer(true)} className="btn-primary flex-1" data-testid="health-check-yes">Yes</button>
                <button onClick={() => answer(false)} className="btn-ghost flex-1" data-testid="health-check-no">No</button>
              </div>
            </div>
          ) : (
            <div data-testid="health-check-result">
              <div className="flex items-baseline gap-3">
                <span className="font-display text-6xl font-black tracking-tight text-metallic" data-testid="health-check-score">
                  {score}/{QUESTIONS.length}
                </span>
                <span className="text-sm text-[#A8B4C2]">protected areas</span>
              </div>
              <p className="mt-6 font-display text-2xl font-bold tracking-tight text-white md:text-3xl" data-testid="health-check-verdict">
                {tier.title}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-[#C9D2DE] md:text-base">{tier.body}</p>
              {gaps.length > 0 && (
                <ul className="mt-6 space-y-2" data-testid="health-check-gaps">
                  {gaps.map((g) => (
                    <li key={g} className="flex gap-3 text-sm text-[#C9D2DE]">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-brand" />
                      {g}
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-10 border-t border-white/10 pt-8">
                <Link to="/contact?mode=book" className="btn-primary w-full" data-testid="health-check-book-cta">
                  Book a free consultation
                </Link>
                {sent ? (
                  <p className="mt-6 border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-[#C9D2DE]" data-testid="health-check-emailed">
                    Results received — the CEO will review your score and reply personally.
                  </p>
                ) : (
                  <form onSubmit={submit} className="mt-8">
                    <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#A8B4C2]">
                      Want a written take on your score?
                    </p>
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <input required minLength={2} className="field" placeholder="Your name" value={form.name} onChange={setF("name")} data-testid="health-check-name-input" />
                      <input className="field" placeholder="Company (optional)" value={form.company} onChange={setF("company")} data-testid="health-check-company-input" />
                    </div>
                    <input required type="email" className="field mt-4" placeholder="Work email" value={form.email} onChange={setF("email")} data-testid="health-check-email-input" />
                    <button type="submit" disabled={busy} className="btn-ghost mt-4 w-full disabled:opacity-50" data-testid="health-check-submit-button">
                      {busy ? "Sending…" : "Send my results to Sempresera"}
                    </button>
                  </form>
                )}
                <button onClick={reset} className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#A8B4C2] transition-colors duration-200 hover:text-brand" data-testid="health-check-retake">
                  <RotateCcw size={14} /> Retake the check
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
